import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { BRAND, HERO_SCENES } from "@/data/brand";
import HeroFX from "@/components/home/HeroFX";
import HeroVideo from "@/components/home/HeroVideo";

const SCENES = HERO_SCENES;
const SCENE_MS = 7000;
const EASE = [0.22, 1, 0.36, 1];

// ─────────────────────────────────────────────────────────────────────────
// COMMAND-CENTER HUD: live timecode counter (mm:ss.ms style)
// ─────────────────────────────────────────────────────────────────────────
function useLiveTimecode() {
  const [t, setT] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (now) => {
      setT(now - start);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  const totalCs = Math.floor(t / 10);
  const mm = String(Math.floor(totalCs / 6000)).padStart(2, "0");
  const ss = String(Math.floor(totalCs / 100) % 60).padStart(2, "0");
  const cs = String(totalCs % 100).padStart(2, "0");
  return `${mm}:${ss}.${cs}`;
}

// Corner bracket — minimal cinematic frame (responsive padding)
const CornerBracket = ({ position, accent = "gold" }) => {
  const color = accent === "gold" ? "rgba(217,203,179,0.6)" : "rgba(160,200,255,0.55)";
  const styles = {
    tl: { top: 70, left: 10, borderTop: `1px solid ${color}`, borderLeft: `1px solid ${color}` },
    tr: { top: 70, right: 10, borderTop: `1px solid ${color}`, borderRight: `1px solid ${color}` },
    bl: { bottom: 10, left: 10, borderBottom: `1px solid ${color}`, borderLeft: `1px solid ${color}` },
    br: { bottom: 10, right: 10, borderBottom: `1px solid ${color}`, borderRight: `1px solid ${color}` },
  };
  return (
    <div
      className="absolute pointer-events-none z-[26] w-5 h-5 sm:w-7 sm:h-7"
      style={styles[position]}
      aria-hidden="true"
    />
  );
};

export default function Hero() {
  const [active, setActive] = useState(0);
  const containerRef = useRef(null);
  const timecode = useLiveTimecode();

  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % SCENES.length), SCENE_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const next = SCENES[(active + 1) % SCENES.length];
    const img = new Image();
    img.src = next.image;
  }, [active]);

  const scene = SCENES[active];
  const nextScene = SCENES[(active + 1) % SCENES.length];

  return (
    <section
      ref={containerRef}
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-black"
      style={{ height: "100vh", minHeight: 720 }}
    >
      {/* ============================================================ */}
      {/* LAYER 1 — Per-scene cinematic renderer (video / image)        */}
      {/* ============================================================ */}
      {scene.renderMode === "video"
        ? <HeroVideo scene={scene} />
        : <HeroFX scene={scene} activeKey={active} sceneMs={SCENE_MS} />}

      {/* Extra atmospheric haze + edge darkening for cinema feel ----- */}
      <div
        className="absolute inset-0 pointer-events-none z-[6]"
        style={{
          background:
            "radial-gradient(ellipse 90% 65% at 50% 50%, transparent 35%, rgba(0,8,28,0.45) 75%, rgba(0,4,18,0.85) 100%)",
        }}
      />

      {/* ============================================================ */}
      {/* LAYER 2 — Cinema letterbox bars (top & bottom)                */}
      {/* ============================================================ */}
      <div
        className="absolute top-0 left-0 right-0 z-[24] pointer-events-none"
        style={{ height: "11vh", background: "linear-gradient(to bottom, rgba(0,0,0,0.95) 0%, rgba(0,4,15,0.8) 60%, transparent 100%)" }}
      />
      <div className="absolute z-[24] pointer-events-none" style={{ top: "11vh", left: 0, right: 0, height: "1px", background: "linear-gradient(90deg, transparent 0%, rgba(198,168,107,0.45) 50%, transparent 100%)" }} />
      <div
        className="absolute bottom-0 left-0 right-0 z-[24] pointer-events-none"
        style={{ height: "9vh", background: "linear-gradient(to top, #000 0%, #00040f 70%, transparent 100%)" }}
      />
      <div className="absolute z-[24] pointer-events-none" style={{ bottom: "9vh", left: 0, right: 0, height: "1px", background: "linear-gradient(90deg, transparent 0%, rgba(198,168,107,0.45) 50%, transparent 100%)" }} />

      {/* Corner brackets — cinematic framing ------------------------ */}
      <CornerBracket position="tl" accent={scene.accent} />
      <CornerBracket position="tr" accent={scene.accent} />
      <CornerBracket position="bl" accent={scene.accent} />
      <CornerBracket position="br" accent={scene.accent} />

      {/* ============================================================ */}
      {/* LAYER 3 — TOP HUD STRIP: positioned BELOW global nav         */}
      {/* ============================================================ */}
      <div
        className="absolute left-0 right-0 z-[25] flex items-center justify-between px-4 sm:px-8 pointer-events-none font-mono text-[0.55rem] sm:text-[0.6rem] tracking-[0.28em] sm:tracking-[0.32em] uppercase text-white/65"
        style={{ top: "76px", height: "28px" }}
      >
        <div className="flex items-center gap-4 pointer-events-auto">
          <span className="text-gold/85 font-medium" data-testid="hero-hud-brand">
            <span className="hidden sm:inline">AG // FIELD REPORT</span>
            <span className="sm:hidden">AG // FIELD</span>
          </span>
        </div>

        <div className="hidden md:flex items-center gap-3 text-white/55">
          <span className="text-glow-soft tabular-nums" data-testid="hero-hud-timecode">REC ● {timecode}</span>
        </div>

        <div className="flex items-center gap-4">
          <AnimatePresence mode="wait">
            <motion.span
              key={`status-${active}`}
              initial={{ opacity: 0, x: 6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="hidden lg:inline text-white/60"
              data-testid="hero-hud-status"
            >
              {scene.status}
            </motion.span>
          </AnimatePresence>
          <span className="inline-flex items-center gap-2 text-gold">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[0.55rem] sm:text-[0.58rem] tracking-[0.3em] sm:tracking-[0.34em]">LIVE</span>
          </span>
        </div>
      </div>

      {/* ============================================================ */}
      {/* LAYER 4 — CENTER LARGE TITLE CARD (cinematic chapter mark)  */}
      {/* ============================================================ */}
      <div className="absolute inset-0 z-[22] flex items-center justify-center pointer-events-none px-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={`title-${active}`}
            initial={{ opacity: 0, scale: 1.06, filter: "blur(16px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 0.98, filter: "blur(10px)" }}
            transition={{ duration: 1.1, ease: EASE }}
            className="text-center max-w-[90vw]"
            data-testid={`hero-title-card-${scene.slug}`}
          >
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.25 }}
              className="font-mono text-[0.7rem] tracking-[0.5em] uppercase mb-5"
              style={{ color: scene.accent === "gold" ? "rgba(217,203,179,0.85)" : "rgba(160,200,255,0.9)" }}
              data-testid="hero-chapter-label"
            >
              <span className="text-gold/80">{scene.chapter.replace("Chapter ", "Ch. ")}</span>
              <span className="mx-3 text-white/30">/</span>
              <span>{scene.locale}</span>
            </motion.div>

            <h1
              className="font-display-tight text-white uppercase tracking-[-0.04em] leading-[0.92]"
              style={{ fontSize: "clamp(2.6rem, 7vw, 7rem)" }}
              data-testid="hero-headline"
            >
              <span className="block">
                {scene.title.split(" ").slice(0, -1).join(" ") || scene.title}
                {scene.title.split(" ").length > 1 ? " " : ""}
                <span
                  className={scene.accent === "gold" ? "gold-metallic font-display font-light" : "font-display font-light"}
                  style={{
                    textShadow:
                      scene.accent === "gold"
                        ? "0 0 40px rgba(198,168,107,0.4)"
                        : "0 0 40px rgba(0,170,255,0.35)",
                  }}
                >
                  {scene.title.split(" ").length > 1 ? scene.title.split(" ").slice(-1)[0] : ""}
                </span>
              </span>
            </h1>

            {/* hairline divider */}
            <motion.div
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ duration: 1.1, ease: EASE, delay: 0.6 }}
              className="mx-auto mt-7 h-px w-32 origin-center"
              style={{
                background: scene.accent === "gold"
                  ? "linear-gradient(90deg, transparent, rgba(217,203,179,0.85), transparent)"
                  : "linear-gradient(90deg, transparent, rgba(160,200,255,0.85), transparent)",
              }}
            />

            <motion.p
              initial={{ opacity: 0, y: 12, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
              className="mt-6 max-w-2xl mx-auto text-white/80 text-base md:text-lg font-light leading-[1.55]"
              data-testid="hero-subline"
            >
              {scene.subtitle}
            </motion.p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ============================================================ */}
      {/* LAYER 5 — BOTTOM HUD: chyron + next-up preview              */}
      {/* ============================================================ */}
      <div
        className="absolute bottom-0 left-0 right-0 z-[25] hidden sm:flex items-end justify-between px-8 pb-3 pointer-events-none font-mono text-[0.65rem] tracking-[0.28em] uppercase"
        style={{ height: "9vh" }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={`chyron-${active}`}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 6 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="flex flex-col gap-1"
            data-testid="hero-hud-chyron"
          >
            <span className="text-gold/80 text-[0.6rem] tracking-[0.34em]">{scene.coords}</span>
            <span className="text-white/55 text-[0.6rem]">{scene.situation}</span>
          </motion.div>
        </AnimatePresence>

        <div className="hidden md:flex flex-col items-end gap-1">
          <span className="text-white/35 text-[0.6rem]">NEXT</span>
          <AnimatePresence mode="wait">
            <motion.span
              key={`next-${active}`}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="text-gold/80 text-[0.6rem] tracking-[0.32em]"
              data-testid="hero-hud-next"
            >
              ▶ {nextScene.title}
            </motion.span>
          </AnimatePresence>
        </div>
      </div>

      {/* ============================================================ */}
      {/* LAYER 6 — Floating CTA dock (bottom-center, above letterbox) */}
      {/* Mobile: full-width band; Desktop: centered pill              */}
      {/* ============================================================ */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.2, ease: EASE }}
        className="absolute z-[27] flex items-center gap-2 sm:gap-5 px-3 sm:px-6 py-2.5 sm:py-3 rounded-full pointer-events-auto justify-center
                   left-4 right-4 sm:left-1/2 sm:right-auto sm:w-auto sm:-translate-x-1/2"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 52px)",
          background: "rgba(8,14,32,0.65)",
          backdropFilter: "blur(24px) saturate(140%)",
          border: "1px solid rgba(198,168,107,0.28)",
          boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7), 0 0 40px rgba(0,170,255,0.08)",
        }}
      >
        <Link
          to="/free-claim-evaluation"
          data-testid="hero-cta-primary"
          className="btn-gold group inline-flex items-center justify-center gap-2 sm:gap-3 !py-2.5 sm:!py-2 !px-4 sm:!px-5 !text-[0.7rem] sm:!text-sm whitespace-nowrap flex-1 sm:flex-none"
        >
          <span className="hidden sm:inline">Start Free Claim Evaluation</span>
          <span className="sm:hidden">Free Evaluation</span>
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
        </Link>
        <span className="inline-block w-px h-5 bg-white/15" />
        <a
          href={BRAND.phoneHref}
          data-testid="hero-cta-secondary"
          className="inline-flex items-center justify-center gap-2 font-mono text-[0.65rem] sm:text-[0.7rem] tracking-[0.18em] sm:tracking-[0.2em] uppercase text-white/80 hover:text-gold transition-colors whitespace-nowrap px-2"
        >
          <Phone className="w-3.5 h-3.5 text-gold" />
          <span>Call</span>
        </a>
      </motion.div>

      {/* ============================================================ */}
      {/* LAYER 7 — Scene progress strip + chapter ticks (bottom edge) */}
      {/* ============================================================ */}
      <div className="absolute left-1/2 z-[27] -translate-x-1/2 flex flex-col items-center gap-3" style={{ bottom: 14 }} data-testid="hero-scene-progress">
        <div className="flex items-center gap-1.5">
          {SCENES.map((s, i) => (
            <button
              key={s.slug}
              onClick={() => setActive(i)}
              aria-label={`Go to ${s.title}`}
              data-testid={`hero-scene-tick-${i}`}
              className="group focus:outline-none pointer-events-auto"
            >
              <span
                className={`block h-px transition-all duration-700 ${i === active ? "w-7 bg-gold" : "w-3 bg-white/15 group-hover:bg-white/40"}`}
                style={i === active ? { boxShadow: "0 0 8px rgba(198,168,107,0.6)" } : {}}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
